import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MoreVertical,
  Wand2,
  Trash2,
  ArrowRightLeft,
  UserPlus,
  X,
  Loader2,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Inline pair-row actions: move to another heat, remove from heat.
 *
 * Props:
 * - heat: current heat
 * - pairIndex: which pair in heat.pairs
 * - otherHeats: list of heats in the same round excluding self
 * - api, headers, onChanged
 */
export function PairActionsMenu({ heat, pairIndex, otherHeats, api, headers, onChanged }) {
  const move = async (targetHeatId) => {
    try {
      await axios.post(
        `${api}/heats/${heat.heat_id}/move-pair`,
        { pair_index: pairIndex, target_heat_id: targetHeatId },
        headers
      );
      toast.success("Pair moved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove this pair from the heat?")) return;
    try {
      await axios.delete(
        `${api}/heats/${heat.heat_id}/pair/${pairIndex}`,
        headers
      );
      toast.success("Pair removed");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-1.5 rounded hover:bg-white/10 text-[#A1A1AA]"
          onClick={(e) => e.stopPropagation()}
          data-testid={`pair-actions-${heat.heat_id}-${pairIndex}`}
          aria-label="Pair actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0f0f12] border-white/10 text-white w-56">
        <DropdownMenuLabel className="text-[#A1A1AA] text-[10px] uppercase tracking-wider">Move to heat</DropdownMenuLabel>
        {otherHeats.length === 0 ? (
          <DropdownMenuItem disabled className="text-[#52525B]">No other heats in round</DropdownMenuItem>
        ) : (
          otherHeats.map((h) => (
            <DropdownMenuItem
              key={h.heat_id}
              onClick={() => move(h.heat_id)}
              data-testid={`pair-move-to-${h.heat_number}`}
            >
              <ArrowRightLeft className="w-3 h-3 mr-2 text-[#FFD700]" />
              Heat #{h.heat_number} ({h.pairs?.length || 0})
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={remove}
          className="text-red-400 focus:text-red-300"
          data-testid={`pair-remove-${heat.heat_id}-${pairIndex}`}
        >
          <Trash2 className="w-3 h-3 mr-2" />
          Remove from heat
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Heat-level actions: auto-fill, add manual pair, delete (with merge), etc.
 */
export function HeatActionsMenu({ heat, otherHeats, api, headers, onChanged, onOpenAdd }) {
  const [busy, setBusy] = useState(false);

  const hasEmptySlots =
    heat.pairs?.some(
      (p) => !p.leader_id || !p.follower_id
    ) || false;

  const autoFill = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${api}/heats/${heat.heat_id}/auto-fill`,
        {},
        headers
      );
      const la = r.data?.leaders_added || 0;
      const fa = r.data?.followers_added || 0;
      if (la === 0 && fa === 0) {
        toast.warning("No replacement dancers found — pool exhausted for this round");
      } else {
        toast.success(r.data?.message || `Auto-filled ${la} leader(s), ${fa} follower(s)`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (mergeIntoId) => {
    const msg = mergeIntoId
      ? `Move all pairs into the selected heat and delete this heat?`
      : `Permanently delete this heat and its scores?`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const qs = mergeIntoId ? `?merge_into=${mergeIntoId}` : "";
      await axios.delete(`${api}/heats/${heat.heat_id}${qs}`, headers);
      toast.success(mergeIntoId ? "Merged + deleted" : "Heat deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      {hasEmptySlots && (
        <Button
          size="sm"
          variant="outline"
          className="btn-outline"
          onClick={autoFill}
          disabled={busy}
          data-testid={`heat-auto-fill-${heat.heat_number}`}
        >
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1 text-[#FFD700]" />}
          Auto-fill empty
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1.5 rounded hover:bg-white/10 text-[#A1A1AA]"
            data-testid={`heat-actions-${heat.heat_number}`}
            aria-label="Heat actions"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#0f0f12] border-white/10 text-white w-56">
          <DropdownMenuItem onClick={onOpenAdd} data-testid={`heat-add-pair-${heat.heat_number}`}>
            <UserPlus className="w-3 h-3 mr-2 text-emerald-400" />
            Add couple from pool…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={autoFill} disabled={!hasEmptySlots || busy} data-testid={`heat-auto-fill-menu-${heat.heat_number}`}>
            <Wand2 className="w-3 h-3 mr-2 text-[#FFD700]" />
            Auto-fill empty slots
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuLabel className="text-[#A1A1AA] text-[10px] uppercase tracking-wider">Delete heat</DropdownMenuLabel>
          {otherHeats.length > 0 &&
            otherHeats.map((h) => (
              <DropdownMenuItem
                key={h.heat_id}
                onClick={() => remove(h.heat_id)}
                data-testid={`heat-merge-into-${h.heat_number}`}
              >
                <ArrowRightLeft className="w-3 h-3 mr-2 text-[#FFD700]" />
                Move pairs to Heat #{h.heat_number}, then delete
              </DropdownMenuItem>
            ))}
          <DropdownMenuItem onClick={() => remove(null)} className="text-red-400" data-testid={`heat-delete-${heat.heat_number}`}>
            <Trash2 className="w-3 h-3 mr-2" />
            Delete heat (lose all pairs)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Add pair to a heat — fully automatic.
 *
 * No dancer selection — the system picks the next-best available L + F
 * from the ranked pool (advancing first, then next-best non-advancing).
 */
export function AddPairDialog({ heat, open, onOpenChange, api, headers, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load a preview of what will be added (top of each pool)
  useEffect(() => {
    if (!open || !heat) return;
    let alive = true;
    setLoading(true);
    setPreview(null);
    axios
      .get(`${api}/heats/${heat.heat_id}/available-dancers`, headers)
      .then((r) => {
        if (!alive) return;
        const nextL = (r.data?.leaders?.advancing?.[0]) || (r.data?.leaders?.next_best?.[0]) || (r.data?.leaders?.others?.[0]) || null;
        const nextF = (r.data?.followers?.advancing?.[0]) || (r.data?.followers?.next_best?.[0]) || (r.data?.followers?.others?.[0]) || null;
        setPreview({
          leader: nextL,
          follower: nextF,
          totals: {
            L: (r.data?.leaders?.advancing?.length || 0) + (r.data?.leaders?.next_best?.length || 0) + (r.data?.leaders?.others?.length || 0),
            F: (r.data?.followers?.advancing?.length || 0) + (r.data?.followers?.next_best?.length || 0) + (r.data?.followers?.others?.length || 0),
          },
        });
      })
      .catch(() => {
        if (alive) toast.error("Failed to load preview");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, heat, api, headers]);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${api}/heats/${heat.heat_id}/add-pair-auto`, {}, headers);
      toast.success(r.data?.message || "Couple added");
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-400" />
            Add couple to Heat #{heat?.heat_number}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-[#A1A1AA] mt-1">
          The system will automatically pick the next-best available dancers from the pool
          (advancing first, then highest-ranked non-advancing).
        </p>

        <div className="mt-3 space-y-2">
          {loading || !preview ? (
            <p className="text-center text-[#A1A1AA] py-4"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading preview…</p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="glass rounded-lg p-3" data-testid="add-pair-preview-leader">
                  <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1">Next Leader</p>
                  {preview.leader ? (
                    <p className="text-sm">
                      <span className="text-[#FFD700] font-bold mr-2">#{preview.leader.bib_number || "?"}</span>
                      {preview.leader.name}
                    </p>
                  ) : (
                    <p className="text-[#52525B] text-xs italic">None available</p>
                  )}
                  <p className="text-[10px] text-[#52525B] mt-1">{preview.totals.L} leader(s) remain in pool</p>
                </div>
                <div className="glass rounded-lg p-3" data-testid="add-pair-preview-follower">
                  <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-1">Next Follower</p>
                  {preview.follower ? (
                    <p className="text-sm">
                      <span className="text-[#FF007F] font-bold mr-2">#{preview.follower.bib_number || "?"}</span>
                      {preview.follower.name}
                    </p>
                  ) : (
                    <p className="text-[#52525B] text-xs italic">None available</p>
                  )}
                  <p className="text-[10px] text-[#52525B] mt-1">{preview.totals.F} follower(s) remain in pool</p>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" className="text-[#A1A1AA]" onClick={() => onOpenChange(false)} disabled={saving} data-testid="add-pair-cancel">
              <X className="w-3 h-3 mr-1" /> Cancel
            </Button>
            <Button
              className="btn-gold"
              disabled={saving || loading || !preview || (!preview.leader && !preview.follower)}
              onClick={submit}
              data-testid="add-pair-submit"
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
              Add to heat
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
